import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { PrimengExportsModule } from '../primeng-exports.module';
import { UtilityService } from '../services/utility.service';

interface ErrorState {
  icon: string;
  title: string;
  body: string;
  canRetry: boolean;
}

const SESSION_ENDED: ErrorState = {
  icon: 'lock',
  title: 'ERRORS.PAGE.SESSION_ENDED_TITLE',
  body: 'ERRORS.PAGE.SESSION_ENDED_BODY',
  canRetry: false
};

// There is no dashboard to go back to without the token HCP passes in.
const NOT_FOUND: ErrorState = {
  icon: 'search_off',
  title: 'ERRORS.PAGE.NOT_FOUND_TITLE',
  body: 'ERRORS.PAGE.NOT_FOUND_BODY',
  canRetry: false
};

const UNAVAILABLE: ErrorState = {
  icon: 'cloud_off',
  title: 'ERRORS.PAGE.UNAVAILABLE_TITLE',
  body: 'ERRORS.PAGE.UNAVAILABLE_BODY',
  canRetry: true
};

/** Same-app paths only, so the page can't be used to send users elsewhere. */
export function isSafeReturnUrl(url: unknown): url is string {
  return (
    typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
  );
}

/**
 * The whole page is unavailable: a quiet centred state with one next step.
 * `?status=` picks the copy. The status code itself never shows: it means
 * nothing to the people reading the page.
 */
@Component({
  selector: 'app-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
  imports: [PrimengExportsModule, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorPageComponent implements AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly utilityService = inject(UtilityService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly queryParams = toSignal(this.route.queryParams, {
    initialValue: {} as Record<string, string | undefined>
  });

  readonly status = computed(() => {
    const status = Number(this.queryParams()['status']);
    return Number.isInteger(status) && status > 0 ? status : null;
  });

  readonly state = computed(() => {
    switch (this.status()) {
      case 401:
        return SESSION_ENDED;
      case 404:
        return NOT_FOUND;
      case null:
      default:
        return UNAVAILABLE;
    }
  });

  ngAfterViewInit(): void {
    // Embedded, the iframe is sized to the reported content height.
    const height = this.elementRef.nativeElement.offsetHeight;
    this.utilityService.emitPropToParent('contentHeight', height / 14);
  }

  onRetry(): void {
    const returnUrl = this.queryParams()['returnUrl'];
    if (isSafeReturnUrl(returnUrl)) {
      this.router.navigateByUrl(returnUrl).catch((error: unknown) => {
        console.error('Navigation failed.', error);
      });
      return;
    }
    window.location.reload();
  }
}
